import type {
   MapCenterLocationCord,
   MapMetrics,
   MapViewportBound,
} from '@/types/map/map-types.ts';
import type {
   MarkerIconSize,
   MarkerImageType,
   MarkerInfo,
   SelectedMarker,
} from '@/types/map/marker/marker-types.ts';
import type { SelectedSources, SourceSubCategory } from '@/types/map/source-types.ts';
import { defineStore } from 'pinia';
import { computed, reactive, ref, shallowRef, watch } from 'vue';
import { MILES_IN_METERS, USER_LOCATION_DEFAULT_ZOOM } from '@/config/map-config.ts';
import { applyMarkerStyle } from '@/services/marker-service.ts';

export const useMapUiStore = defineStore('map-ui', () => {
   // ==============================================================================================
   // CORE MAP STATE
   // ==============================================================================================

   /** Reference to the Google Maps instance */
   const map = shallowRef<google.maps.Map | null>(null);

   /** Current radius of the map view in meters */
   const mapRadiusInMeters = ref<number>(100);

   /** Current center coordinates and zoom level of the map */
   const mapCenterLocationCord = reactive<MapCenterLocationCord>({
      latitude: 0,
      longitude: 0,
      zoom: 0,
   });

   /** Current viewport bounds of the map */
   const mapViewportBound = reactive<MapViewportBound>({
      NE: null,
      SW: null,
   });

   // ==============================================================================================
   // USER LOCATION STATE
   // ==============================================================================================

   /** User's location coordinates and default zoom level */
   const userLocation = reactive<MapCenterLocationCord>({
      latitude: 0,
      longitude: 0,
      zoom: USER_LOCATION_DEFAULT_ZOOM,
   });

   // ==============================================================================================
   // SOURCE & MARKER STATE
   // ==============================================================================================

   /** Selected sources configuration */
   const selectedSources: SelectedSources = reactive({
      subcategory: [],
      nasaLocationDurationInDays: 7, // default 7 days
   });

   /** Marker info window state */
   const isMarkerInfoWindowOpen = ref<boolean>(false);

   /** Selected marker information */
   const selectedMarker = reactive<SelectedMarker>({
      type: null,
      subcategory: null,
      markerData: null,
      markerImgSrc: null,
      markerImgType: null,
      coords: {
         lat: 0,
         lng: 0,
      },
      title: null,
      size: 'sm',
   });

   /** Currently loading marker subcategories */
   const markersLoadingState = reactive<SourceSubCategory[]>([]);

   // ==============================================================================================
   // COMPUTED PROPERTIES
   // ==============================================================================================

   /** Converts map radius from meters to miles */
   const mapRadiusInMiles = computed(() => {
      return mapRadiusInMeters.value / MILES_IN_METERS;
   });

   /** Formatted bounding box string with coordinate normalization */
   const mapBoundingBox = computed(() => {
      const swLng = mapViewportBound.SW?.lng() || 0;
      const swLat = mapViewportBound.SW?.lat() || 0;
      const neLng = mapViewportBound.NE?.lng() || 0;
      const neLat = mapViewportBound.NE?.lat() || 0;

      let normalizedSwLng = swLng;
      let normalizedNeLng = neLng;

      // Handle international date line crossing
      if (normalizedNeLng < normalizedSwLng) {
         if (normalizedSwLng > 0) {
            normalizedSwLng -= 360;
         }
         else if (normalizedNeLng < 0) {
            normalizedNeLng += 360;
         }
      }

      // Ensure coordinates are within valid ranges
      normalizedSwLng = Math.max(-180, Math.min(180, normalizedSwLng));
      normalizedNeLng = Math.max(-180, Math.min(180, normalizedNeLng));
      const normalizedSwLat = Math.max(-90, Math.min(90, swLat));
      const normalizedNeLat = Math.max(-90, Math.min(90, neLat));

      return `${normalizedSwLng.toFixed(4)},${normalizedSwLat.toFixed(4)},${normalizedNeLng.toFixed(4)},${normalizedNeLat.toFixed(4)}`;
   });

   // ==============================================================================================
   // MAP ACTIONS
   // ==============================================================================================

   /**
    * Sets the core map data from the provided object
    * @param mapMetrics
    */
   const setMapMetricsData = (mapMetrics: MapMetrics) => {
      mapCenterLocationCord.latitude = mapMetrics.mapCenterLocationCord.latitude;
      mapCenterLocationCord.longitude = mapMetrics.mapCenterLocationCord.longitude;
      mapCenterLocationCord.zoom = mapMetrics.mapCenterLocationCord.zoom;

      mapViewportBound.NE = mapMetrics.mapViewportBound.NE;
      mapViewportBound.SW = mapMetrics.mapViewportBound.SW;

      mapRadiusInMeters.value = mapMetrics.mapRadiusInMeters;
   };

   /**
    * Sets the selected marker information from the provided object
    * @param markerInfo
    * @param markerImgSrc
    */
   const setSelectedMarkerInfo = (markerInfo: MarkerInfo, markerImgSrc: HTMLImageElement | HTMLDivElement) => {
      selectedMarker.type = markerInfo.type;
      selectedMarker.subcategory = markerInfo.subcategory;
      selectedMarker.markerImgType = markerInfo.markerImgType;
      selectedMarker.markerData = markerInfo.markerData;
      selectedMarker.coords.lat = markerInfo.coords.lat;
      selectedMarker.coords.lng = markerInfo.coords.lng;
      selectedMarker.title = markerInfo.title;
      selectedMarker.markerImgSrc = markerImgSrc;
      selectedMarker.size = markerInfo.icon.size;

      // open the info window
      isMarkerInfoWindowOpen.value = true;
   };

   /**
    * Clears the selected marker information
    */
   function clearSelectedMarkerInfo() {
      selectedMarker.type = null;
      selectedMarker.subcategory = null;
      selectedMarker.markerData = null;
      selectedMarker.markerImgSrc = null;
      selectedMarker.markerImgType = null;
      selectedMarker.coords.lat = 0;
      selectedMarker.coords.lng = 0;
      selectedMarker.title = null;
   }

   /**
    * Sets the map instance reference
    * @param mapInstance
    */
   function setMapInstance(mapInstance: google.maps.Map) {
      map.value = mapInstance;
   }

   // ==============================================================================================
   // WATCHERS & LISTENERS
   // ==============================================================================================

   /**
    * Watches for changes in the selected marker's image source and type
    * Updates marker image to show selected/unselected state when sidebar is open
    */
   watch(() => [
      selectedMarker.markerImgSrc,
      selectedMarker.markerImgType,
      selectedMarker.size,
   ], (newValue, oldValue) => {
      // Deselect old marker
      if (oldValue?.[0]) {
         applyMarkerStyle(
            oldValue[0] as HTMLImageElement | HTMLDivElement,
            oldValue[1] as MarkerImageType,
            oldValue[2] as MarkerIconSize,
            false, // unselected
         );
      }

      // Select a new marker
      if (newValue?.[0]) {
         applyMarkerStyle(
            newValue[0] as HTMLImageElement | HTMLDivElement,
            newValue[1] as MarkerImageType,
            newValue[2] as MarkerIconSize,
            true, // selected
         );
      }
   }, { deep: true });

   /**
    * Watches for changes in the info window open state
    * Resets selection state when the info window is closed
    */
   watch(isMarkerInfoWindowOpen, () => {
      if (!isMarkerInfoWindowOpen.value) {
         clearSelectedMarkerInfo();
      }
   });

   // ==============================================================================================
   // RESET FUNCTION
   // ==============================================================================================

   /**
    * Resets the store to its initial state
    */
   const $reset = () => {
      // Core map state
      map.value = null;
      mapRadiusInMeters.value = 100;
      mapCenterLocationCord.latitude = 0;
      mapCenterLocationCord.longitude = 0;
      mapCenterLocationCord.zoom = 0;
      mapViewportBound.NE = null;
      mapViewportBound.SW = null;

      // User location
      userLocation.latitude = 0;
      userLocation.longitude = 0;
      userLocation.zoom = USER_LOCATION_DEFAULT_ZOOM;

      // Sources and markers
      selectedSources.subcategory.length = 0;
      selectedSources.nasaLocationDurationInDays = 7;

      // info window
      isMarkerInfoWindowOpen.value = false;

      // Selected marker info
      clearSelectedMarkerInfo();

      // Currently loading marker subcategories
      markersLoadingState.length = 0;
   };

   return {
      // State
      map,
      mapRadiusInMeters,
      mapCenterLocationCord,
      mapViewportBound,
      userLocation,
      selectedSources,
      selectedMarker,
      isMarkerInfoWindowOpen,
      markersLoadingState,

      // Computed
      mapRadiusInMiles,
      mapBoundingBox,

      // Map Actions
      setMapMetricsData,
      setSelectedMarkerInfo,
      setMapInstance,

      // Reset
      $reset,
   };
});
